import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FixedActions from '../components/FixedActions';
import { blogPosts } from '../data/blogData';
import { Calendar, User, ArrowRight, Search } from 'lucide-react';
import { trackSearch } from '../utils/analytics';

const Blogs = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', ...new Set(blogPosts.map(post => post.category))];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    // Track search after user types at least 3 characters
    if (term.length >= 3) {
      trackSearch(term);
    }
  };

  const featuredPost = filteredPosts[0] || blogPosts[0];
  const otherPosts = filteredPosts.slice(1);

  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://rightshade.in';

  return (
    <div style={{ background: '#FAFBFC' }}>
      <Helmet>
        <title>Sports Court Construction Blog | Expert Insights | Right Shade Bangalore</title>
        <meta name="description" content="Expert guides on sports court construction in Bangalore. Learn about pickleball, badminton, padel, football turf, cricket pitch costs, materials & ROI. 15+ years experience." />
        <meta name="keywords" content="sports court construction blog, pickleball court construction bangalore, badminton court builders, padel court cost, football turf installation, cricket pitch construction, Right Shade" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${siteUrl}/blogs`} />
        <meta property="og:title" content="Sports Court Construction Blog | Right Shade Bangalore" />
        <meta property="og:description" content="Expert guides on sports court construction in Bangalore. Pickleball, badminton, padel, football turf & more." />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Sports Court Construction Blog | Right Shade Bangalore" />
        <meta name="twitter:description" content="Expert guides on sports court construction in Bangalore. Pickleball, badminton, padel, football turf & more." />
        
        {/* Canonical URL */}
        <link rel="canonical" href={`${siteUrl}/blogs`} />
      </Helmet>
      
      <Header />
      <FixedActions />
      
      <section style={{
        paddingTop: '140px',
        paddingBottom: '60px',
        background: 'linear-gradient(135deg, #1A1B1E 0%, #2D2E32 100%)',
        position: 'relative'
      }}>
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h1 style={{
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              fontWeight: 700,
              color: 'white',
              marginBottom: '20px',
              fontFamily: "'Poppins', sans-serif"
            }}>
              Insights & Updates
            </h1>
            <p style={{
              fontSize: 'clamp(1.125rem, 2vw, 1.375rem)',
              color: 'rgba(255,255,255,0.9)',
              lineHeight: 1.6,
              fontFamily: "'Inter', sans-serif"
            }}>
              Explore the latest trends, tips, and stories from the world of sports courts and construction
            </p>
          </div>
        </div>
      </section>

      <section style={{ padding: '20px 0', background: 'white', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
        <div className="container">
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <div style={{ flex: '1', minWidth: '300px', position: 'relative' }}>
              <Search size={20} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#718096' }} />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{
                  width: '100%',
                  padding: '14px 16px 14px 48px',
                  border: '2px solid #E2E8F0',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontFamily: "'Inter', sans-serif"
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '24px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: selectedCategory === category ? 'linear-gradient(135deg, #FFD700, #FFA500)' : '#F7FAFC',
                    color: selectedCategory === category ? 'white' : '#4A5568',
                    fontFamily: "'Inter', sans-serif"
                  }}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {featuredPost && (
        <section style={{ padding: '60px 0' }}>
          <div className="container">
            <div style={{
              background: 'white',
              borderRadius: '24px',
              overflow: 'hidden',
              boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
              display: 'grid',
              gridTemplateColumns: '1.2fr 1fr',
              minHeight: '500px'
            }}>
              <div style={{ position: 'relative' }}>
                <img 
                  src={featuredPost.image} 
                  alt={featuredPost.title} 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <div style={{
                  position: 'absolute',
                  top: '24px',
                  left: '24px',
                  background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                  color: 'white',
                  padding: '8px 20px',
                  borderRadius: '24px',
                  fontSize: '14px',
                  fontWeight: 600
                }}>
                  Featured Article
                </div>
              </div>

              <div style={{ padding: '48px' }}>
                <div style={{
                  display: 'inline-block',
                  background: '#F7FAFC',
                  color: '#4A5568',
                  padding: '6px 16px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  marginBottom: '20px'
                }}>
                  {featuredPost.category}
                </div>

                <h2 style={{
                  fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
                  fontWeight: 700,
                  marginBottom: '16px',
                  color: '#1A1B1E',
                  lineHeight: 1.3,
                  fontFamily: "'Poppins', sans-serif"
                }}>
                  {featuredPost.title}
                </h2>

                <p style={{
                  fontSize: '16px',
                  lineHeight: 1.7,
                  color: '#4A5568',
                  marginBottom: '24px',
                  fontFamily: "'Inter', sans-serif"
                }}>
                  {featuredPost.excerpt}
                </p>

                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '32px', fontSize: '14px', color: '#718096' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <User size={16} />
                    <span>{featuredPost.author}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Calendar size={16} />
                    <span>{new Date(featuredPost.publishDate).toLocaleDateString()}</span>
                  </div>
                </div>

                <Link to={`/blog/${featuredPost.slug}`} className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '14px 28px', fontSize: '16px' }}>
                  Read Full Article <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      <section style={{ padding: '60px 0', paddingTop: '0' }}>
        <div className="container">
          {otherPosts.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '32px' }}>
              {otherPosts.map((post) => (
                <article key={post.id} style={{
                  background: 'white',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease'
                }}>
                  <div style={{ position: 'relative', height: '240px', background: '#f7fafc' }}>
                    <img 
                      src={post.image} 
                      alt={post.title} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#718096;font-size:14px;">Image not available</div>';
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      background: 'rgba(255,255,255,0.95)',
                      color: '#4A5568',
                      padding: '6px 14px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: 600
                    }}>
                      {post.category}
                    </div>
                  </div>

                  <div style={{ padding: '24px' }}>
                    <h3 style={{
                      fontSize: '20px',
                      fontWeight: 600,
                      marginBottom: '12px',
                      color: '#1A1B1E',
                      lineHeight: 1.4,
                      fontFamily: "'Poppins', sans-serif"
                    }}>
                      {post.title}
                    </h3>

                    <p style={{
                      fontSize: '15px',
                      lineHeight: 1.6,
                      color: '#4A5568',
                      marginBottom: '20px',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      {post.excerpt.substring(0, 100)}...
                    </p>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      paddingTop: '16px',
                      borderTop: '1px solid #E2E8F0'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#718096' }}>
                        <Calendar size={14} />
                        <span>{new Date(post.publishDate).toLocaleDateString()}</span>
                      </div>

                      <Link to={`/blog/${post.slug}`} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: '#FFD700',
                        fontSize: '14px',
                        fontWeight: 600,
                        textDecoration: 'none'
                      }}>
                        Read More <ArrowRight size={16} />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 20px', background: 'white', borderRadius: '20px' }}>
              <p style={{ fontSize: '18px', color: '#718096', fontFamily: "'Inter', sans-serif" }}>
                No articles found. Try adjusting your search or filters.
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
      
      <style jsx>{`
        @media (max-width: 1024px) {
          section:nth-of-type(3) > div > div {
            grid-template-columns: 1fr !important;
            gap: 30px !important;
          }
        }
        
        @media (max-width: 768px) {
          section {
            padding: 60px 20px !important;
          }
          
          section:first-of-type {
            padding-top: 100px !important;
            padding-bottom: 60px !important;
          }
          
          section:first-of-type h1 {
            font-size: 2rem !important;
          }
          
          section:first-of-type p {
            font-size: 1rem !important;
          }
          
          /* Search and filter section - remove top padding */
          section:nth-of-type(2) {
            padding: 20px 0 !important;
          }
          
          /* Search and filter */
          section:nth-of-type(2) > div > div {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }
          
          /* Make category buttons full width in mobile */
          section:nth-of-type(2) > div > div > div:last-child {
            width: 100%;
          }
          
          section:nth-of-type(2) > div > div > div:last-child button {
            flex: 1;
            min-width: 80px;
          }
          
          /* Blog grid */
          section:nth-of-type(3) > div > div {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
          
          .container {
            padding: 0 20px !important;
          }
        }
        
        @media (max-width: 480px) {
          section:first-of-type h1 {
            font-size: 1.75rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Blogs;
